import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Filter, Megaphone, DollarSign, Award, Users, ArrowRight,
  Loader2, Star, MapPin, Building, Heart, Share2, ExternalLink,
  ChevronDown, X, Grid, List, TrendingUp, Gift, Zap, Check,
  Briefcase, Coffee, Utensils, ShoppingBag, Home, Car, Dumbbell,
  GraduationCap, Stethoscope, Laptop, Hammer, Scale, Music, PawPrint
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api, useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const CATEGORY_ICONS = {
  'restaurants': Utensils,
  'home-services': Home,
  'automotive': Car,
  'health-wellness': Heart,
  'beauty-spa': Star,
  'professional-services': Briefcase,
  'real-estate': Building,
  'fitness': Dumbbell,
  'education': GraduationCap,
  'pets': PawPrint,
  'entertainment': Music,
  'shopping': ShoppingBag,
  'technology': Laptop,
  'construction': Hammer,
  'legal': Scale,
  'medical': Stethoscope,
  'food-beverage': Coffee,
};

// Business card component
function AffiliateBusinessCard({ business, onPromote }) {
  const [promoting, setPromoting] = useState(false);
  const { user } = useAuth();
  
  const handlePromote = async () => {
    if (!user) {
      toast.error('Please login to become an affiliate');
      return;
    }
    
    setPromoting(true);
    try {
      const response = await fetch(`${API_URL}/api/directory/businesses/${business.id}/promote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('You are now promoting this business!');
        if (onPromote) onPromote(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to join affiliate program');
      }
    } catch (error) {
      toast.error('Failed to join affiliate program');
    } finally {
      setPromoting(false);
    }
  };
  
  const program = business.affiliate_program || {};
  const commissionDisplay = program.commission_type === 'percentage' 
    ? `${program.commission_rate || 10}%`
    : `$${program.commission_amount || program.commission_rate || 10}`;
  
  const CategoryIcon = CATEGORY_ICONS[business.category?.toLowerCase().replace(/\s+/g, '-')] || Building;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="bg-card rounded-2xl border border-white/10 overflow-hidden hover:border-purple-500/30 transition-all group"
      data-testid={`affiliate-business-${business.id}`}
    >
      {/* Header with logo/image */}
      <div className="relative h-32 bg-gradient-to-br from-purple-900/50 to-pink-900/50">
        {business.logo ? (
          <img 
            src={business.logo} 
            alt={business.name}
            className="w-full h-full object-cover"
          />
        ) : business.images?.[0] ? (
          <img 
            src={business.images[0]} 
            alt={business.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CategoryIcon className="w-12 h-12 text-purple-400/50" />
          </div>
        )}
        
        {/* Category badge */}
        <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm text-xs text-white flex items-center gap-1">
          <CategoryIcon className="w-3 h-3" />
          {business.category}
        </div>
        
        {/* Featured badge */}
        {business.is_featured && (
          <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-amber-500/90 text-xs text-black font-medium flex items-center gap-1">
            <Star className="w-3 h-3" />
            Featured
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-white text-lg mb-1 group-hover:text-purple-400 transition-colors line-clamp-1">
          {business.name}
        </h3>
        
        {business.city && business.state && (
          <p className="text-xs text-gray-500 flex items-center gap-1 mb-3">
            <MapPin className="w-3 h-3" />
            {business.city}, {business.state}
          </p>
        )}
        
        <p className="text-sm text-gray-400 line-clamp-2 mb-4">
          {business.description}
        </p>
        
        {/* Affiliate Program Stats */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="text-center p-2 bg-purple-500/10 rounded-lg">
            <p className="text-lg font-bold text-purple-400">{commissionDisplay}</p>
            <p className="text-[10px] text-gray-500">Commission</p>
          </div>
          <div className="text-center p-2 bg-cyan-500/10 rounded-lg">
            <p className="text-lg font-bold text-cyan-400">{program.credit_reward || 10}</p>
            <p className="text-[10px] text-gray-500">Credits</p>
          </div>
          <div className="text-center p-2 bg-green-500/10 rounded-lg">
            <p className="text-lg font-bold text-green-400">{program.cookie_duration || 30}d</p>
            <p className="text-[10px] text-gray-500">Cookie</p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={handlePromote}
            disabled={promoting}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm"
          >
            {promoting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Megaphone className="w-4 h-4 mr-1" />
                Promote
              </>
            )}
          </Button>
          <Link to={`/directory/business/${business.id}`}>
            <Button variant="outline" size="icon" className="border-white/20 hover:bg-white/10">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default function AffiliateMarketplacePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({ total: 0, categories: [] });
  
  const categories = [
    'Restaurants', 'Home Services', 'Automotive', 'Health & Wellness',
    'Beauty & Spa', 'Professional Services', 'Real Estate', 'Fitness',
    'Education', 'Pets', 'Entertainment', 'Shopping', 'Technology'
  ];

  useEffect(() => {
    loadBusinesses();
  }, [selectedCategory]);

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/affiliate/businesses?limit=50`;
      if (selectedCategory) {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data.businesses || []);
        setStats({ total: data.total || 0 });
      }
    } catch (error) {
      console.error('Failed to load businesses:', error);
      toast.error('Failed to load affiliate programs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadBusinesses();
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category === selectedCategory ? '' : category);
  };

  return (
    <DashboardLayout>
      <div className="p-6 pt-20 max-w-7xl mx-auto" data-testid="affiliate-marketplace-page">
        {/* Hero Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <Megaphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Affiliate Marketplace</h1>
                  <p className="text-gray-400 text-sm">Browse and promote businesses to earn commissions</p>
                </div>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-xl">
                <Building className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="text-lg font-bold text-white">{stats.total}</p>
                  <p className="text-[10px] text-gray-500">Active Programs</p>
                </div>
              </div>
              <Link to="/affiliate">
                <Button variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10">
                  My Promotions <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Search & Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search businesses..."
                className="pl-10 bg-white/5 border-white/10 h-11"
              />
            </div>
            <Button type="submit" className="bg-purple-500 hover:bg-purple-600 h-11">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button 
              type="button"
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="border-white/20 h-11"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              <ChevronDown className={cn("w-4 h-4 ml-2 transition-transform", showFilters && "rotate-180")} />
            </Button>
          </form>
          
          {/* Category Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 overflow-hidden"
              >
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-400">Filter by Category</span>
                    {selectedCategory && (
                      <button 
                        onClick={() => setSelectedCategory('')}
                        className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Clear
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((category) => {
                      const catKey = category.toLowerCase().replace(/\s+/g, '-').replace('&', '');
                      const Icon = CATEGORY_ICONS[catKey] || Building;
                      const isSelected = selectedCategory === category;
                      return (
                        <button
                          key={category}
                          onClick={() => handleCategorySelect(category)}
                          className={cn(
                            "px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5",
                            isSelected
                              ? "bg-purple-500 text-white"
                              : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10"
                          )}
                        >
                          <Icon className="w-3 h-3" />
                          {category}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* View Mode Toggle */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-400">
            Showing {businesses.length} affiliate programs
            {selectedCategory && <span className="text-purple-400"> in {selectedCategory}</span>}
          </p>
          <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                "p-2 rounded transition-all",
                viewMode === 'grid' ? "bg-white/10 text-white" : "text-gray-500 hover:text-white"
              )}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                "p-2 rounded transition-all",
                viewMode === 'list' ? "bg-white/10 text-white" : "text-gray-500 hover:text-white"
              )}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Business Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-20">
            <Megaphone className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Affiliate Programs Found</h3>
            <p className="text-gray-400 mb-6">
              {searchQuery || selectedCategory 
                ? 'Try adjusting your search or filters'
                : 'Be the first to create an affiliate program!'}
            </p>
            {(searchQuery || selectedCategory) && (
              <Button 
                onClick={() => { setSearchQuery(''); setSelectedCategory(''); loadBusinesses(); }}
                variant="outline"
                className="border-purple-500/30 text-purple-400"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {businesses.map((business) => (
              <AffiliateBusinessCard 
                key={business.id} 
                business={business}
                onPromote={() => loadBusinesses()}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {businesses.map((business) => {
              const program = business.affiliate_program || {};
              const commissionDisplay = program.commission_type === 'percentage' 
                ? `${program.commission_rate || 10}%`
                : `$${program.commission_amount || program.commission_rate || 10}`;
              const CategoryIcon = CATEGORY_ICONS[business.category?.toLowerCase().replace(/\s+/g, '-')] || Building;
              
              return (
                <motion.div
                  key={business.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-4 p-4 bg-card rounded-xl border border-white/10 hover:border-purple-500/30 transition-all"
                >
                  {/* Logo */}
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-900/50 to-pink-900/50 flex items-center justify-center shrink-0 overflow-hidden">
                    {business.logo ? (
                      <img src={business.logo} alt={business.name} className="w-full h-full object-cover" />
                    ) : (
                      <CategoryIcon className="w-8 h-8 text-purple-400/50" />
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate">{business.name}</h3>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <CategoryIcon className="w-3 h-3" />
                      {business.category}
                      {business.city && <span>• {business.city}, {business.state}</span>}
                    </p>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-center">
                      <p className="text-lg font-bold text-purple-400">{commissionDisplay}</p>
                      <p className="text-[10px] text-gray-500">Commission</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-cyan-400">{program.credit_reward || 10}</p>
                      <p className="text-[10px] text-gray-500">Credits</p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" className="bg-purple-500 hover:bg-purple-600">
                      <Megaphone className="w-4 h-4 mr-1" />
                      Promote
                    </Button>
                    <Link to={`/directory/business/${business.id}`}>
                      <Button variant="outline" size="sm" className="border-white/20">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* How It Works Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 p-6 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-2xl border border-purple-500/20"
        >
          <h2 className="text-xl font-bold text-white mb-6 text-center">How Affiliate Marketing Works</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                <Megaphone className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">1. Choose & Promote</h3>
              <p className="text-sm text-gray-400">Select businesses that match your audience and start promoting with your unique link</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">2. Share & Engage</h3>
              <p className="text-sm text-gray-400">Share on social media, your website, or directly with potential customers</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">3. Earn Rewards</h3>
              <p className="text-sm text-gray-400">Get commissions and publishing credits for every conversion you drive</p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
